<template>
  <div class="app-wrapper">
    <header class="top-header">
      <div class="header-brand">
        <div class="brand-mark">T</div>
        <div class="brand-info">
          <span class="brand-name">thxily</span>
          <span class="brand-sub">管理コンソール</span>
        </div>
      </div>
      <button class="back-btn" v-on:click="goBack">← メニューへ戻る</button>
    </header>
    <main class="main-content">
  <div class="white-card">
    <h2 class="page-title">契約状況確認</h2>
    <div class="content">
      <!-- 読み込み中表示 -->
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-spinner-large"></div>
        <p>契約情報を読み込み中...</p>
      </div>

      <!-- エラー・契約なし表示 -->
      <div v-else-if="errorMessage" class="error-message">
        <div class="error-icon">⚠️</div>
        <h4>エラーが発生しました</h4>
        <p>{{ errorMessage }}</p>
        <button @click="loadContractData" class="button-primary">再読み込み</button>
      </div>

      <div v-else-if="!contractData.has_contract" class="no-contract">
        <div class="no-contract-icon">📋</div>
        <h3>契約情報が見つかりません</h3>
        <p>現在有効な契約がありません。</p>
      </div>

      <!-- メインコンテンツ -->
      <div v-else>
        <!-- 請求概要 -->
        <div class="section">
          <h3>請求概要</h3>
          
          <!-- 確定・未確定状態の大きな表示 -->
          <div class="billing-status-banner" :class="getConfirmationStatusClass()">
            <div class="status-icon">{{ getStatusIcon() }}</div>
            <div class="status-content">
              <h4 class="status-title">{{ getConfirmationStatusText() }}</h4>
              <p class="status-description">{{ getStatusDescription() }}</p>
            </div>
          </div>
          
          <p class="mb-2">
            <span class="font-semibold">対象請求期間:</span> {{ currentMonthName }}度請求分
          </p>
          <p class="text-red-600 font-semibold">
            ※従業員数は毎月5日時点のデータに基づき確定されます。
          </p>
          <p class="text-sm text-gray-500 mt-2">
            現在表示日: {{ formatDate(new Date()) }}
          </p>
        </div>

        <!-- 請求内訳 -->
        <div class="section">
          <h3>請求内訳</h3>
          <div class="card-details">
            <div class="detail-row">
              <span class="detail-label">{{ getEmployeeCountLabel() }}</span>
              <span class="detail-value">{{ usageData.current_month_confirmed_count || usageData.current_user_count }} 名</span>
            </div>
            <!-- 
            <div class="detail-row">
              <span class="detail-label">適用料金階層</span>
              <span class="detail-value">{{ usageData.current_tier?.tier_name || '未設定' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">基本料金</span>
              <span class="detail-value">¥{{ formatAmount(usageData.current_tier?.base_price || 0) }}</span>
            </div> -->
          </div>
          <div class="highlight-amount">
            {{ getBillingAmountLabel() }}: ¥{{ formatAmount(usageData.confirmed_billing_amount || usageData.estimated_billing) }}
          </div>
          <div class="details-content">
            <p class="mb-2">
              <span class="font-bold">確定従業員数について:</span>
              この人数は、毎月5日午前0時時点のシステム利用人数を基に算出されます。
            </p>
          </div>
        </div>

        <!-- 現在の利用状況 -->
        <div class="section">
          <h3>現在の利用状況 (リアルタイム)</h3>
          <div class="card-details">
            <div class="detail-row">
              <span class="detail-label">現在従業員数</span>
              <span class="detail-value">{{ usageData.current_user_count }} 名</span>
            </div>
          </div>
          <p class="text-sm text-gray-600 mt-2">
            この人数は、現在システムをご利用中の従業員数を示しています。<br>
            請求金額には影響しませんが、現在の利用状況の目安としてご参照ください。
          </p>
        </div>

        <!-- 過去の請求との比較 -->
        <!-- <div class="section">
          <h3>過去の請求との比較</h3>
          <div class="card-details">
            <div class="detail-row">
              <span class="detail-label">前月確定従業員数</span>
              <span class="detail-value">{{ usageData.latest_confirmed_count || 0 }} 名</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">前月合計請求金額</span>
              <span class="detail-value">¥{{ formatAmount(calculatePreviousBilling()) }}</span>
            </div>
          </div>
        </div> -->

        <!-- デバッグ情報（開発用） -->
        <div class="section" style="background: #fff3cd; border: 2px solid #ffc107;">
          <h3>デバッグ情報</h3>
          <div style="text-align: left; font-size: 14px;">
            <p><strong>contractData:</strong></p>
            <pre style="font-size: 12px; overflow: auto; background: white; padding: 10px; border-radius: 8px;">{{ JSON.stringify(contractData, null, 2) }}</pre>
            
            <p style="margin-top: 20px;"><strong>条件チェック:</strong></p>
            <ul style="background: white; padding: 15px; border-radius: 8px;">
              <li>contractData.subscription 存在: <strong>{{ !!contractData.subscription ? 'はい ✅' : 'いいえ ❌' }}</strong></li>
              <li v-if="contractData.subscription">subscription.status: <strong>{{ contractData.subscription.status }}</strong></li>
              <li v-if="contractData.subscription">status !== 'CANCELED': <strong>{{ contractData.subscription.status !== 'CANCELED' ? 'はい ✅' : 'いいえ ❌' }}</strong></li>
            </ul>
          </div>
        </div>

        <!-- サブスクリプション管理 -->
        <div class="section">
          <h3>サブスクリプション管理</h3>
          
          <!-- サブスクリプションが存在しない場合 -->
          <div v-if="!contractData.subscription" class="no-subscription-info">
            <div class="no-subscription-icon">📋</div>
            <h4>サブスクリプションが登録されていません</h4>
            <p>現在、有効なサブスクリプションがありません。</p>
            <p class="text-sm text-gray-600 mt-2">
              サブスクリプションは毎月5日に自動的に作成されます。<br>
              または、管理者にお問い合わせください。
            </p>
          </div>
          
          <!-- サブスクリプションが存在する場合 -->
          <div v-else>
            <!-- 解約予定の警告バナー -->
            <div v-if="contractData.cancel_scheduled_at && contractData.subscription.status !== 'CANCELED'" class="cancel-scheduled-banner">
              <div class="warning-icon">⚠️</div>
              <div class="cancel-scheduled-content">
                <h4>解約予定</h4>
                <p>次回更新時（翌月5日）にサブスクリプションが解約されます。</p>
                <p class="text-sm">それまではサービスをご利用いただけます。</p>
              </div>
            </div>
            
            <!-- アクティブなサブスクリプション -->
            <div v-if="contractData.subscription.status !== 'CANCELED'">
            <div class="card-details">
              <div class="detail-row">
                <span class="detail-label">サブスクリプションID</span>
                <span class="detail-value subscription-id">{{ contractData.subscription.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">ステータス</span>
                <span class="detail-value">
                  <span class="status-badge status-success">アクティブ</span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">次回課金日</span>
                <span class="detail-value">{{ formatDate(contractData.subscription.next_charge_date) }}</span>
              </div>
            </div>
            <div class="action-buttons" v-if="!contractData.cancel_scheduled_at">
              <button @click="showCancelDialog = true" class="btn btn-danger" :disabled="isCancelling">
                <span v-if="!isCancelling">サブスクリプションを解約</span>
                <span v-else>解約処理中...</span>
              </button>
            </div>
            <p class="text-sm text-gray-600 mt-2" v-if="!contractData.cancel_scheduled_at">
              ※解約すると、次回更新時（翌月5日）にサブスクリプションが解約されます。<br>
              それまではサービスをご利用いただけます。
            </p>
          </div>
          
          <!-- 解約済みサブスクリプション -->
          <div v-else>
            <div class="canceled-subscription-banner">
              <div class="canceled-icon">🚫</div>
              <div class="canceled-content">
                <h4>サブスクリプション解約済み</h4>
                <p>このサブスクリプションは解約されています。</p>
              </div>
            </div>
            <div class="card-details">
              <div class="detail-row">
                <span class="detail-label">サブスクリプションID</span>
                <span class="detail-value subscription-id">{{ contractData.subscription.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">ステータス</span>
                <span class="detail-value">
                  <span class="status-badge status-error">解約済み</span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">解約日</span>
                <span class="detail-value">{{ formatDate(contractData.subscription.updated) }}</span>
              </div>
            </div>
            <p class="text-sm text-gray-600 mt-2">
              再度ご利用になる場合は、新規でサブスクリプションをお申し込みください。
            </p>
            </div>
          </div>
        </div>

        <!-- 登録クレジットカード情報 -->
        <div class="section">
          <h3>登録クレジットカード情報</h3>
          
          <!-- カード情報がある場合 -->
          <div v-if="contractData.card_info">
            <div class="card-details">
              <div class="detail-row">
                <span class="detail-label">カード番号</span>
                <span class="detail-value">{{ contractData.card_info.card_no_masked }}</span>
              </div>
            </div>
            <div class="action-buttons">
              <button @click="goToCardManagement" class="btn btn-warning">
                クレジットカードを変更
              </button>
            </div>
          </div>
          
          <!-- カード情報がない場合 -->
          <div v-else class="no-card-info">
            <div class="no-card-icon">💳</div>
            <h4>クレジットカードが登録されていません</h4>
            <p>お支払いにはクレジットカードの登録が必要です。</p>
            <div class="action-buttons">
              <button @click="goToCardRegistration" class="btn btn-primary">
                クレジットカードを登録
              </button>
            </div>
          </div>
        </div>

        <!-- 注意事項 -->
        <div class="section">
          <h3>注意事項</h3>
          <ul class="list-disc list-inside space-y-2">
            <li>確定後の従業員数の変更は原則としてできません。</li>
            <li>ご不明な点がある場合は、確定期限日までにお問い合わせください。</li>
          </ul>
          <h4 class="contact-title">お問い合わせ先</h4>
          <p class="mb-2">メールアドレス: <span class="font-semibold">support@example.com</span></p>
        </div>


        <!-- 過去の請求履歴 -->
        <div class="section">
          <h3>過去の請求履歴</h3>
          
          <!-- 読み込み中表示 -->
          <div v-if="loadingHistory" class="loading-area">
            <div class="loading-spinner"></div>
            <p>履歴を読み込み中...</p>
          </div>
          
          <!-- 履歴データがない場合 -->
          <div v-else-if="!paymentHistory || paymentHistory.length === 0" class="no-history">
            <div class="no-history-icon">📄</div>
            <p>請求履歴がありません</p>
          </div>
          
          <!-- 履歴一覧 -->
          <div v-else class="history-list">
            <div class="history-item" v-for="payment in paymentHistory" :key="payment.id">
              <div class="card-details">
                <div class="detail-row">
                  <span class="detail-label">決済日時</span>
                  <span class="detail-value">{{ formatDate(payment.created) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">請求金額</span>
                  <span class="detail-value">¥{{ formatAmount(payment.amount) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">ステータス</span>
                  <span class="detail-value">
                    <span class="status-badge" :class="payment.status_class">
                      {{ payment.status }}
                    </span>
                  </span>
                </div>
                <div class="detail-row" v-if="payment.order_id">
                  <span class="detail-label">注文ID</span>
                  <span class="detail-value order-id">{{ payment.order_id }}</span>
                </div>
                <div class="detail-row" v-if="payment.card_no_masked">
                  <span class="detail-label">カード</span>
                  <span class="detail-value">{{ payment.card_no_masked }} <span v-if="payment.brand" class="brand">{{ payment.brand }}</span></span>
                </div>
              </div>
              <!-- 請求書・領収書ダウンロードボタン -->
              <div class="download-buttons" v-if="payment.status === '売上確定'">
                <button @click="downloadInvoice(payment)" class="btn-download">
                  <span class="download-icon">📄</span> 請求書
                </button>
                <button @click="downloadReceipt(payment)" class="btn-download">
                  <span class="download-icon">🧾</span> 領収書
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 解約確認ダイアログ -->
    <div v-if="showCancelDialog" class="modal-overlay" @click.self="showCancelDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>サブスクリプション解約の確認</h3>
        </div>
        <div class="modal-body">
          <div class="warning-icon">⚠️</div>
          <p class="warning-text">本当にサブスクリプションを解約しますか？</p>
          <div class="cancel-info">
            <p><strong>解約予約について：</strong></p>
            <ul>
              <li>解約は<strong>即座には実行されません</strong></li>
              <li><strong>次回更新時（翌月5日）</strong>に解約されます</li>
              <li>それまではサービスをご利用いただけます</li>
              <li>次回以降の自動課金が停止されます</li>
            </ul>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCancelDialog = false" class="btn btn-secondary" :disabled="isCancelling">
            キャンセル
          </button>
          <button @click="cancelSubscription" class="btn btn-danger" :disabled="isCancelling">
            <span v-if="!isCancelling">解約する</span>
            <span v-else>処理中...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 解約予約完了ダイアログ -->
    <div v-if="showCancelSuccessDialog" class="modal-overlay" @click.self="showCancelSuccessDialog = false">
      <div class="modal-content">
        <div class="modal-header success">
          <h3>解約予約完了</h3>
        </div>
        <div class="modal-body">
          <div class="success-icon">✅</div>
          <p class="success-text">サブスクリプションの解約予約が完了しました。</p>
          <div class="cancel-info">
            <p><strong>次回更新時（翌月5日）に解約されます。</strong></p>
            <p class="text-sm text-gray-600 mt-2">
              それまではサービスを引き続きご利用いただけます。
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCancelSuccessDialog" class="btn btn-primary">
            閉じる
          </button>
        </div>
      </div>
    </div>
  </div>
  </main>
  </div>
</template>


<script>
import axios from "axios";

export default {
  name: 'ContractStatusView',
  mounted() {
    console.log('=== Component mounted ===');
    console.log('loadContractData を呼び出します');
    this.loadContractData();
    console.log('loadUsageStatistics を呼び出します');
    this.loadUsageStatistics();
  },
  data() {
    return {
      isLoading: true,
      errorMessage: '',
      contractData: {
        has_contract: false,
        subscription: null,
        plan: null,
        card_info: null
      },
      usageData: {
        current_user_count: 0,
        current_month_confirmed_count: 0,
        current_month_confirmed_status: "計算中",
        latest_confirmed_count: 0,
        latest_confirmed_date: null,
        next_confirmation_date: null,
        current_tier: null,
        estimated_billing: 0,
        confirmed_billing_amount: 0,
        next_billing_date: null,
        monthly_trends: []
      },
      // 新しいデータ
      showDetails: false,
      // 請求履歴データ
      paymentHistory: [],
      loadingHistory: false,
      // 解約関連
      showCancelDialog: false,
      showCancelSuccessDialog: false,
      isCancelling: false
    };
  },
  computed: {
    // 現在の日付が5日までかどうかを判定
    isBeforeConfirmationDate() {
      const today = new Date();
      return today.getDate() <= 5;
    },
    
    // 現在の月名
    currentMonthName() {
      const today = new Date();
      return `${today.getFullYear()}年${today.getMonth() + 1}月`;
    },
    
    // 来月の月名
    nextMonthName() {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return `${nextMonth.getFullYear()}年${nextMonth.getMonth() + 1}月`;
    },
    
    // 今月末の請求日
    currentMonthBillingDate() {
      const today = new Date();
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return this.formatDate(lastDay);
    },
    
    // 来月末の請求日
    nextMonthBillingDate() {
      const today = new Date();
      const nextMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return this.formatDate(nextMonthLastDay);
    },
    
    // 今月分の状態テキスト
    currentMonthStatusText() {
      return this.isBeforeConfirmationDate ? '未確定' : '確定';
    }
  },
  methods: {
    // 詳細の展開/折りたたみ
    toggleDetails() {
      this.showDetails = !this.showDetails;
    },

    async loadContractData() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        console.log('=== loadContractData 開始 ===');
        console.log('リクエスト URL: /contract_status');
        const response = await axios.get("/contract_status");
        console.log('レスポンス URL:', response.config.url);
        console.log('レスポンス全体:', response);
        console.log('サブスクリプションステータス:', response.data?.data?.subscription?.status);
        console.log('サブスクリプションID:', response.data?.data?.subscription?.id);
        
        if (response.data.status === "SUCCESS") {
          this.contractData = response.data.data;
          
          // デバッグ用ログ
          console.log('Contract Data:', this.contractData);
          console.log('Has Subscription:', !!this.contractData.subscription);
          if (this.contractData.subscription) {
            console.log('Subscription Status:', this.contractData.subscription.status);
          }
          
          // 契約がある場合は決済履歴も取得
          if (this.contractData.has_contract) {
            await this.loadPaymentHistory();
          }
        } else {
          throw new Error(response.data.message || '契約情報の取得に失敗しました');
        }
      } catch (error) {
        console.error('Contract data loading error:', error);
        this.errorMessage = error.response?.data?.detail || error.message || '契約情報の読み込み中にエラーが発生しました';
      } finally {
        this.isLoading = false;
      }
    },

    async loadPaymentHistory() {
      this.loadingHistory = true;
      
      try {
        const response = await axios.get("/payment_history?limit=5");
        
        if (response.data.status === "SUCCESS") {
          this.paymentHistory = response.data.data.payments || [];
        }
      } catch (error) {
        console.error('Payment history loading error:', error);
        // 決済履歴のエラーは表示しない（メイン機能ではないため）
      } finally {
        this.loadingHistory = false;
      }
    },

    formatAmount(amount) {
      return Number(amount || 0).toLocaleString('ja-JP');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },

    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'status-active';
        case 'inactive':
        case 'suspended':
          return 'status-inactive';
        case 'cancelled':
          return 'status-cancelled';
        default:
          return 'status-unknown';
      }
    },

    getStatusText(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'アクティブ';
        case 'inactive':
          return '停止中';
        case 'suspended':
          return '一時停止';
        case 'cancelled':
          return 'キャンセル済み';
        default:
          return '不明';
      }
    },

    goToCardManagement() {
      this.$router.push('/credit-card');
    },

    goToCardRegistration() {
      this.$router.push('/credit-card');
    },

    goToPlans() {
      // プラン選択画面への遷移（実装予定）
      this.$router.push('/payment');
    },

    goBack() {
      this.$router.go(-1);
    },

    retry() {
      this.loadContractData();
      this.loadUsageStatistics();
      // this.loadPricingTiers();
      this.loadUserCountHistory();
    },

    async loadUsageStatistics() {
      try {
        const response = await axios.get("/usage_statistics");
        console.log(response);
        if (response.data.status === "SUCCESS") {
          this.usageData = response.data.data;
        } else {
          console.error('Usage statistics loading failed:', response.data.message);
        }
      } catch (error) {
        console.error('Usage statistics loading error:', error);
      }
    },

    // async loadPricingTiers() {
    //   try {
    //     const response = await axios.get("/pricing_tiers");
        
    //     if (response.data.status === "SUCCESS") {
    //       this.pricingTiers = response.data.data.pricing_tiers;
    //     } else {
    //       console.error('Pricing tiers loading failed:', response.data.message);
    //     }
    //   } catch (error) {
    //     console.error('Pricing tiers loading error:', error);
    //   }
    // },

    async loadUserCountHistory() {
      try {
        const response = await axios.get("/user_count_history");
        
        if (response.data.status === "SUCCESS") {
          this.userCountHistory = response.data.data.history;
          this.historyStats = response.data.data.statistics;
        } else {
          console.error('User count history loading failed:', response.data.message);
        }
      } catch (error) {
        console.error('User count history loading error:', error);
      }
    },


    getBarHeight(userCount) {
      if (this.historyStats.max_user_count === 0) return 0;
      return Math.max(10, (userCount / this.historyStats.max_user_count) * 100);
    },

    formatShortDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day}`;
    },

    getBillingChangeClass() {
      if (!this.usageData.current_tier || this.usageData.latest_confirmed_count === 0) {
        return 'change-neutral';
      }
      
      const currentBilling = this.usageData.estimated_billing;
      const previousBilling = this.usageData.current_tier.base_price + 
                             (this.usageData.latest_confirmed_count * this.usageData.current_tier.price_per_user);
      const change = currentBilling - previousBilling;
      
      if (change > 0) {
        return 'change-positive';
      } else if (change < 0) {
        return 'change-negative';
      } else {
        return 'change-neutral';
      }
    },

    // 従業員数ラベルを取得
    getEmployeeCountLabel() {
      const isConfirmed = this.usageData.current_month_confirmed_status === '確定';
      if (isConfirmed) {
        return `確定従業員数 (${this.currentMonthName}5日時点)`;
      } else {
        return `未確定従業員数 (現在時点)`;
      }
    },

    // 請求金額ラベルを取得
    getBillingAmountLabel() {
      const isConfirmed = this.usageData.current_month_confirmed_status === '確定';
      return isConfirmed ? '請求金額' : '予想請求金額';
    },

    // 確定状態テキストを取得
    getConfirmationStatusText() {
      return this.usageData.current_month_confirmed_status || 'エラー';
    },

    // 確定状態クラスを取得
    getConfirmationStatusClass() {
      const status = this.usageData.current_month_confirmed_status;
      if (status === '確定') {
        return 'status-confirmed';
      } else {
        return 'status-unconfirmed';
      }
    },

    // 状態アイコンを取得
    getStatusIcon() {
      const status = this.usageData.current_month_confirmed_status;
      if (status === '確定') {
        return '';
      } else {
        return '';
      }
    },

    // 状態説明を取得
    getStatusDescription() {
      const status = this.usageData.current_month_confirmed_status;
      if (status === '確定') {
        return `${this.currentMonthName}5日時点の従業員数で請求金額が確定しました`;
      } else {
        return `${this.currentMonthName}5日に従業員数が確定され、請求金額が決定されます`;
      }
    },

    // サブスクリプション解約
    async cancelSubscription() {
      this.isCancelling = true;
      
      try {
        const response = await axios.post("/cancel_subscription");
        
        if (response.data.status === "SUCCESS") {
          this.showCancelDialog = false;
          this.showCancelSuccessDialog = true;
          
          // 契約情報を再読み込み
          await this.loadContractData();
        } else {
          throw new Error(response.data.message || 'サブスクリプションの解約に失敗しました');
        }
      } catch (error) {
        console.error('Subscription cancellation error:', error);
        alert(error.response?.data?.detail || error.message || 'サブスクリプションの解約中にエラーが発生しました');
        this.showCancelDialog = false;
      } finally {
        this.isCancelling = false;
      }
    },

    // 解約完了ダイアログを閉じる
    closeCancelSuccessDialog() {
      this.showCancelSuccessDialog = false;
    },

    // 請求書PDFダウンロード
    async downloadInvoice(payment) {
      await this.downloadPdf(payment, 'invoice', '請求書');
    },

    // 領収書PDFダウンロード
    async downloadReceipt(payment) {
      await this.downloadPdf(payment, 'receipt', '領収書');
    },

    // PDF共通ダウンロード処理
    async downloadPdf(payment, docType, docName) {
      try {
        const endpoint = docType === 'invoice' ? '/download_invoice' : '/download_receipt';
        const response = await axios.get(endpoint, {
          params: { payment_id: payment.id },
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const dateStr = payment.created ? payment.created.replace(/\//g, '').substring(0, 8) : 'unknown';
        link.download = `${docType}_${dateStr}.pdf`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`${docName}のダウンロードに失敗しました:`, error);
        alert(`${docName}のダウンロードに失敗しました。`);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap');

/* ===== 共通レイアウト ===== */
.app-wrapper {
  min-height: 100vh;
  background: #DDE2EC;
  font-family: 'Noto Sans JP', 'Segoe UI', sans-serif;
}

.top-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: #1E2A3A;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.header-brand { display: flex; align-items: center; gap: 12px; }

.brand-mark {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #4F8EF7, #7C5CFC);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; font-weight: 800; color: #fff;
}

.brand-info { display: flex; flex-direction: column; line-height: 1.2; }
.brand-name { font-size: 1rem; font-weight: 700; color: #fff; }
.brand-sub  { font-size: 0.68rem; color: #8A9BB0; }

.back-btn {
  padding: 8px 20px; border-radius: 8px;
  border: 1.5px solid #94A3B8; background: transparent;
  color: #94A3B8; font-size: 0.85rem; font-weight: 600;
  cursor: pointer; font-family: inherit;
  transition: background 0.2s, color 0.2s;
}
.back-btn:hover { background: #94A3B8; color: #1E2A3A; }

.main-content {
  max-width: 960px; margin: 0 auto;
  padding: 40px 24px 80px;
}

.white-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.10);
  padding: 32px 40px;
}

.page-title {
  font-size: 1.3rem; font-weight: 700;
  color: #1E293B; margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E2E8F0;
}

/* ===== ボタン ===== */
.btn {
  padding: 10px 24px; border-radius: 8px; border: none;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background 0.2s;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}

.btn-primary {
  background: #3B82F6; color: #fff;
}
.btn-primary:hover:not(:disabled) { background: #2563EB; }

.btn-secondary {
  border: 1.5px solid #CBD5E1; background: #fff;
  color: #475569;
}
.btn-secondary:hover:not(:disabled) { background: #F1F5F9; }

.btn-warning {
  background: #F59E0B; color: #fff;
}
.btn-warning:hover:not(:disabled) { background: #D97706; }

.btn-danger {
  background: #EF4444; color: #fff;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }

.btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ===== コンテンツエリア ===== */
.content {
  padding: 0;
}

/* ===== セクション ===== */
.section {
  background: #F8FAFC;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
}

.section h3 {
  color: #1E293B;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: left;
}

/* ===== カード詳細 ===== */
.card-details {
  background: white;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
  border: 1.5px solid #E2E8F0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #F1F5F9;
  gap: 12px;
}
.detail-row:last-child { border-bottom: none; }

.detail-label {
  color: #64748B; font-size: 0.85rem; font-weight: 600;
  min-width: 140px;
}

.detail-value {
  color: #1E293B; font-weight: 600; font-size: 0.9rem;
  text-align: right; flex: 1;
}

/* ===== 金額ハイライト ===== */
.highlight-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10B981;
  text-align: center;
  margin: 16px 0;
  padding: 14px;
  background: white;
  border-radius: 10px;
  border: 2px solid #10B981;
}

/* ===== ローディング・エラー ===== */
.loading-screen {
  text-align: center;
  padding: 60px 0;
  color: #64748B;
}

.loading-spinner-large {
  display: inline-block;
  width: 40px; height: 40px;
  border: 4px solid #E2E8F0;
  border-radius: 50%;
  border-top-color: #3B82F6;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.error-message,
.no-contract {
  text-align: center;
  padding: 48px 0;
  color: #64748B;
}

.error-icon,
.no-contract-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.no-card-info,
.no-subscription-info {
  text-align: center;
  padding: 32px 16px;
}

.no-card-icon,
.no-history-icon,
.no-subscription-icon {
  font-size: 3rem;
  margin-bottom: 12px;
}

.no-card-info h4,
.no-subscription-info h4 {
  color: #1E293B;
  margin-bottom: 6px;
  font-size: 1rem;
  font-weight: 700;
}

.no-card-info p,
.no-subscription-info p,
.no-history p {
  color: #64748B;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

/* ===== アクションボタン ===== */
.action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

/* ===== 詳細コンテンツ ===== */
.details-content {
  margin-top: 12px;
  padding: 14px 16px;
  background: white;
  border-radius: 8px;
  border: 1.5px solid #E2E8F0;
  font-size: 0.85rem;
  color: #475569;
  text-align: left;
}

/* ===== 請求履歴 ===== */
.loading-area {
  text-align: center;
  padding: 32px 0;
  color: #64748B;
}

.loading-spinner {
  width: 24px; height: 24px;
  border: 3px solid #E2E8F0;
  border-top: 3px solid #3B82F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

.no-history {
  text-align: center;
  padding: 32px 0;
  color: #64748B;
  font-size: 0.9rem;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.history-item {
  background: white;
  border-radius: 10px;
  border: 1.5px solid #E2E8F0;
  overflow: hidden;
}

.history-item .detail-label { font-size: 0.85rem; }
.history-item .detail-value { font-size: 0.9rem; }
.history-item .status-badge { font-size: 0.8rem; padding: 4px 12px; }
.history-item .order-id { font-size: 0.85rem; }

/* ===== ステータスバッジ ===== */
.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}

.status-success { background: #D1FAE5; color: #065F46; }
.status-error   { background: #FEE2E2; color: #991B1B; }
.status-pending { background: #FEF3C7; color: #92400E; }
.status-unknown { background: #F1F5F9; color: #64748B; }

.order-id { font-family: monospace; font-size: 0.82rem; }
.subscription-id { font-family: monospace; font-size: 0.82rem; }

.brand {
  text-transform: uppercase;
  font-weight: 700;
  color: #3B82F6;
}

/* ===== 請求状態バナー ===== */
.billing-status-banner {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 10px;
  margin-bottom: 16px;
  border: 2px solid;
}

.billing-status-banner.status-confirmed {
  background: #D1FAE5; border-color: #10B981; color: #065F46;
}
.billing-status-banner.status-unconfirmed {
  background: #FEF3C7; border-color: #F59E0B; color: #92400E;
}

.status-icon { font-size: 1.8rem; margin-right: 14px; line-height: 1; }
.status-content { flex: 1; }
.status-title { font-size: 1.1rem; font-weight: 700; margin: 0 0 6px 0; }
.status-description { font-size: 0.85rem; margin: 0; opacity: 0.9; }

/* ===== 解約バナー ===== */
.canceled-subscription-banner {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #FEE2E2;
  border: 2px solid #EF4444;
  border-radius: 10px;
  margin-bottom: 16px;
}
.canceled-icon { font-size: 1.8rem; margin-right: 14px; }
.canceled-content { flex: 1; }
.canceled-content h4 { margin: 0 0 4px 0; font-size: 1rem; font-weight: 700; color: #991B1B; }
.canceled-content p  { margin: 0; font-size: 0.85rem; color: #7F1D1D; }

.cancel-scheduled-banner {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #FEF3C7;
  border: 2px solid #F59E0B;
  border-radius: 10px;
  margin-bottom: 16px;
}
.cancel-scheduled-content { flex: 1; }
.cancel-scheduled-content h4 { margin: 0 0 6px 0; font-size: 1rem; font-weight: 700; color: #92400E; }
.cancel-scheduled-content p  { margin: 0 0 4px 0; font-size: 0.85rem; color: #78350F; }

/* ===== ダウンロードボタン ===== */
.download-buttons {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #E2E8F0;
}

.btn-download {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1.5px solid #3B82F6;
  border-radius: 8px;
  color: #3B82F6;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-download:hover {
  background: #3B82F6;
  color: white;
}
.download-icon { font-size: 1rem; }

/* ===== モーダル ===== */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.24);
  animation: modalSlideIn 0.25s ease-out;
}

@keyframes modalSlideIn {
  from { opacity: 0; transform: translateY(-32px); }
  to   { opacity: 1; transform: translateY(0); }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1.5px solid #E2E8F0;
  background: #1E2A3A;
  color: white;
  border-radius: 16px 16px 0 0;
}
.modal-header.success { background: #10B981; }
.modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }

.modal-body {
  padding: 28px 24px;
  text-align: center;
}

.warning-icon,
.success-icon {
  font-size: 3rem;
  margin-bottom: 12px;
}

.warning-text,
.success-text {
  font-size: 1rem;
  font-weight: 700;
  color: #1E293B;
  margin-bottom: 20px;
}

.cancel-info {
  background: #F8FAFC;
  border-radius: 10px;
  padding: 16px 20px;
  text-align: left;
  margin-top: 16px;
  border: 1.5px solid #E2E8F0;
}
.cancel-info p { margin: 0 0 10px 0; font-size: 0.9rem; font-weight: 600; color: #1E293B; }
.cancel-info ul { margin: 0; padding-left: 20px; list-style-type: disc; }
.cancel-info li { margin-bottom: 6px; color: #64748B; font-size: 0.85rem; }

.modal-footer {
  padding: 16px 24px;
  border-top: 1.5px solid #E2E8F0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.modal-footer .btn { padding: 8px 20px; }

/* ===== ユーティリティ ===== */
.font-semibold { font-weight: 600; }
.font-bold     { font-weight: 700; }
.text-red-600  { color: #DC2626; }
.text-sm       { font-size: 0.875rem; }
.text-gray-500 { color: #64748B; }
.text-gray-600 { color: #475569; }
.mb-2 { margin-bottom: 0.5rem; }
.mt-2 { margin-top: 0.5rem; }

.contact-title {
  font-size: 0.95rem; font-weight: 700;
  color: #1E293B; margin: 16px 0 8px 0;
}

.list-disc { list-style-type: disc; padding-left: 20px; }
.list-inside { list-style-position: inside; }
.space-y-2 > * + * { margin-top: 0.5rem; }

@keyframes spin { to { transform: rotate(360deg); } }

/* ===== デバッグ情報 ===== */
.section pre { font-size: 0.75rem; overflow: auto; background: white; padding: 10px; border-radius: 8px; }

.button-primary {
  padding: 10px 24px; border-radius: 8px; border: none;
  background: #3B82F6; color: #fff;
  font-size: 0.9rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background 0.2s;
  margin-top: 12px;
}
.button-primary:hover { background: #2563EB; }

/* ===== 旧スタイルの残留参照を上書き ===== */
.contract-status-container {
  /* 旧ルート要素は app-wrapper 内の白カードに包まれるため不要 */
}


</style>
